import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useStyles from 'isomorphic-style-loader/useStyles';

import { IconReactionPositiveSmall, IconMessageSmall } from '@sololearnorg/sol';
import { AuthService, Container, i18n } from '../../../../symphony';

import {
	questionsFilerTypeAlias,
	SlDiscussContext,
} from '../../global-constants';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { ActionType, RequestType } from '../../global-interface';
import { SlDataService } from '../../services/sl-data.service';
import { Meta } from '../Meta/Meta';
import { getQuestionAlias } from '../../utils/helpers';
import { QuestionsListLoading } from './QuestionsListLoading';
import { EmptyQuestions } from '../EmptyQuestions/EmptyQuestions';
import { AskQuestion } from '../AskQuestion/AskQuestion';
import { useComponentWillMount } from '../../../../shared/public/SlUtils/useComponentWillMount';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { LocalizedLink } from '../../../../shared/public/LocalizedLink/LocalizedLink';

import s from './QuestionsList.scss';

export function QuestionsList() {
	useStyles(s);
	const isLoggedin = Container.take('global', AuthService).isLoggedIn();
	const { actions$ } = Container.take(SlDiscussContext, SlActions);
	const [searchParams] = useSearchParams();
	const currentPageNumber = +searchParams.get('page') || 1;
	const questionsListSearchText = searchParams.get('query') || '';
	const questionsListFilter = searchParams.get('ordering')
		|| Object.values(questionsFilerTypeAlias)[0];
	const isMyAnswers = questionsListFilter === 'MyAnswers';
	const isMyQuestions = questionsListFilter === 'MyQuestions';

	useComponentWillMount(() => {
		if (!((isMyAnswers || isMyQuestions) && !isLoggedin)) {
			actions$.next({
				type: ActionType.OnDiscussQuestionsListMount,
				requestType: RequestType.Parallel,
				data: {
					questionsListFilter,
					questionsListSearchText,
					currentPageNumber,
				},
			});
		}
	});

	const {
		questionsList$: questionsList,
		isQuestionsListLoading$: isQuestionsListLoading,
	} = useContainerData(SlDiscussContext, SlDataService, [
		'questionsList$',
		'isQuestionsListLoading$',
	]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [currentPageNumber]);

	if (isQuestionsListLoading) {
		return <QuestionsListLoading />;
	}

	if (
		!questionsList?.length
		|| ((isMyAnswers || isMyQuestions) && !isLoggedin)
	) {
		return (
			<EmptyQuestions
				isMyAnswers={isMyAnswers}
				isMyQuestions={isMyQuestions}
			/>
		);
	}
	const onQuestionClick = (id: number) => {
		actions$.next({
			type: ActionType.OnQuestionClick,
			requestType: RequestType.Parallel,
			data: {
				id,
			},
		});
	};
	const onTagClick = (tag: string, questionId: number) => {
		actions$.next({
			type: ActionType.OnTagClick,
			requestType: RequestType.Parallel,
			data: {
				alias: tag,
				questionId: String(questionId),
			},
		});
	};

	return (
		<div className="discuss-questions-list">
			{questionsList
				?.filter((question) => !question.hidden)
				.map((question) => (
					<div
						className="discuss-questions-list__item"
						key={question.id}
					>
						<div className="discuss-questions-list__item__left-part">
							<LocalizedLink
								className="discuss-questions-list__item__title"
								to={`/Discuss/${question.id}/${getQuestionAlias(
									question.title,
								)}`}
								onClick={() => onQuestionClick(question.id)}
							>
								{question.title}
							</LocalizedLink>
							<div className="discuss-questions-list__item__tags">
								{question.tags?.map((tag) => (
									<LocalizedLink
										to={`/Discuss/?query=${tag}`}
										className="discuss-questions-list__item__tags__tag"
										key={tag}
										onClick={() => onTagClick(tag, question.id)}
									>
										{tag}
									</LocalizedLink>
								))}
							</div>
							<div className="discuss-questions-list__item__info">
								<div className="discuss-questions-list__item__info__votes">
									<IconReactionPositiveSmall />
									{' '}
									{i18n.t('web-discuss.votes-count', {
										count: question.votes,
									})}
								</div>
								<LocalizedLink
									to={`/Discuss/${question.id
									}/${getQuestionAlias(question.title)}`}
									onClick={() => onQuestionClick(question.id)}
									className="discuss-questions-list__item__info__answers"
								>
									<IconMessageSmall />
									{' '}
									{i18n.t('web-discuss.answers-count', {
										count: question.answers,
									})}
								</LocalizedLink>
							</div>
						</div>
						<Meta
							date={question.date}
							avatarUrl={question.avatarUrl}
							userName={question.userName}
							userID={question.userId}
							badge={question.badge}
						/>
					</div>
				))}
			<AskQuestion />
		</div>
	);
}
